// CoreLogic API service using Next.js API
// Uses environment-based configuration for local vs deployed API
import sessionManager from './sessionManager';

// Environment configuration
const CURRENT_ENV = process.env.REACT_APP_CURRENT_ENV || 'local';

// API server configuration based on environment
const getApiBaseUrl = () => {
  // Use the same environment variable as the main API client
  const base = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  // Remove trailing slash if present
  const cleanBase = base.replace(/\/$/, '');
  return `${cleanBase}/api/corelogic`;
};

const API_SERVER_URL = getApiBaseUrl();

console.log('[CoreLogic] API Configuration:', {
  environment: CURRENT_ENV,
  baseUrl: API_SERVER_URL
});

// Mock data for development/testing - matches real CoreLogic API structure
const MOCK_PROPERTY_DATA = {
  clip: "1016926183",
  v1PropertyId: "48085:264432880",
  propertyAddress: {
    streetAddress: "3748 HOMEPLACE DR",
    city: "CELINA",
    state: "TX",
    zipCode: "75009",
    county: "COLLIN"
  },
  propertyAPN: {
    fipsCode: "48085",
    apnParcelNumberFormatted: "R-11530-00H-0080-1",
    universalParcelId: "264432880"
  }
};

// Mock ownership data based on the actual API response structure
const MOCK_OWNERSHIP_DATA = {
  data: {
    clip: "1016926183",
    currentOwners: {
      ownerNames: [
        {
          sequenceNumber: 1,
          fullName: "TARIQ MUHAMMAD",
          firstNameAndMiddleInitial: "MUHAMMAD",
          lastName: "TARIQ",
          firstName: "MUHAMMAD",
          middleName: null,
          isCorporate: false
        },
        {
          sequenceNumber: 2,
          fullName: "TARIQ MUNIZA",
          firstNameAndMiddleInitial: "MUNIZA",
          lastName: "TARIQ",
          firstName: "MUNIZA",
          middleName: null,
          isCorporate: false
        }
      ],
      relationshipTypeCode: null,
      ownerEtalCode: null,
      occupancyCode: "O",
      ownershipRightsCode: null
    },
    currentOwnerMailingInfo: {
      mailingAddress: {
        careOfName: null,
        streetAddress: "3748 HOMEPLACE DR",
        city: "CELINA",
        state: "TX",
        zipCode: "75009",
        carrierRoute: "R013"
      },
      ownerMailingOptOutIndicator: null
    }
  }
};

// Frontend service - calls API server which handles CoreLogic authentication

/**
 * DEBUG FUNCTION: Log the complete API response structure
 * This helps us understand exactly what fields are available
 */
export function debugApiResponse(data, endpoint) {
  console.group(`🔍 [CoreLogic Debug] ${endpoint} Response Structure`);
  console.log('Full response:', JSON.stringify(data, null, 2));

  if (data && typeof data === 'object') {
    console.log('Available top-level keys:', Object.keys(data));

    // Log nested structures
    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        console.log(`${key} keys:`, Object.keys(value));
        if (Array.isArray(value)) {
          console.log(`${key} array length:`, value.length);
          if (value.length > 0) {
            console.log(`${key} first item keys:`, Object.keys(value[0]));
          }
        }
      }
    });
  }

  console.groupEnd();
}

/**
 * Helper to get auth headers
 */
const getAuthHeaders = async () => {
  const token = await sessionManager.getAccessToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { "Authorization": `Bearer ${token}` } : {})
  };
};

/**
 * Step 1: Search for property using v2 API endpoint
 * @param {Object} params
 * @param {string} params.streetAddress
 * @param {string} params.zipCode
 * @returns {Promise<Object>} Property search results
 */
async function searchProperty({ streetAddress, zipCode }) {
  try {
    const url = `${API_SERVER_URL}/search?streetAddress=${encodeURIComponent(streetAddress)}&zipCode=${encodeURIComponent(zipCode)}`;
    const headers = await getAuthHeaders();

    console.log('[CoreLogic] Searching property for:', streetAddress, zipCode);
    console.log('[CoreLogic] Search URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[CoreLogic] Search API Error:', response.status, errorData);
      throw new Error(errorData.message || `CoreLogic search API error: ${response.status}`);
    }

    const data = await response.json();

    // DEBUG: Log the search response structure
    debugApiResponse(data, 'Property Search');

    console.log('[CoreLogic] Search success response:', data);

    // Log owner information if available
    if (data?.items?.[0]?.ownership?.currentOwners?.ownerNames) {
      const owners = data.items[0].ownership.currentOwners.ownerNames;
      const validOwners = owners.filter(owner =>
        owner && (owner.fullName || (owner.firstName && owner.lastName))
      );
      console.log('[CoreLogic] Owner analysis:', {
        total: owners.length,
        valid: validOwners.length,
        nullCount: owners.length - validOwners.length
      });
    }

    return data;
  } catch (err) {
    console.error('[CoreLogic] Property search failed:', err);

    // If error in development, return mock data
    if (CURRENT_ENV === 'local') {
      console.log('[CoreLogic] API server error. Returning mock search data.');
      return {
        metadata: {
          pageNumber: 1,
          pageSize: 1,
          totalRecords: 1,
          totalPages: 1
        },
        items: [MOCK_PROPERTY_DATA]
      };
    }

    throw err;
  }
}

/**
 * Step 2: Fetch ownership data using CLIP ID
 * @param {string} clipId - The CLIP ID from property search
 * @returns {Promise<Object>} Ownership data
 */
async function fetchOwnershipData(clipId) {
  try {
    const url = `${API_SERVER_URL}/ownership/${clipId}`;
    const headers = await getAuthHeaders();

    console.log('[CoreLogic] Fetching ownership for CLIP:', clipId);
    console.log('[CoreLogic] Ownership URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`Ownership API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('[CoreLogic] Ownership data:', data);

    // DEBUG: Log the ownership response structure
    debugApiResponse(data, 'Ownership');

    return data;
  } catch (err) {
    console.error('[CoreLogic] Ownership fetch failed:', err);

    // If error in development, return mock data
    if (CURRENT_ENV === 'local') {
      console.log('[CoreLogic] API server error. Returning mock ownership data.');
      return MOCK_OWNERSHIP_DATA;
    }

    throw err;
  }
}

/**
 * Main function: Fetch property data by address using two-step API flow
 * @param {Object} params
 * @param {string} params.streetAddress
 * @param {string} params.city
 * @param {string} params.state
 * @param {string} [params.zipCode]
 * @returns {Promise<Object>} Combined property and ownership data
 */
export async function fetchPropertyByAddress({ streetAddress, city, state, zipCode }) {
  // Validate required parameters
  if (!streetAddress) {
    throw new Error('Street address is required');
  }
  if (!zipCode) {
    throw new Error('Zip code is required for property search');
  }

  try {
    console.log('[CoreLogic] Starting two-step property lookup for:', streetAddress, zipCode);

    // Step 1: Search for property
    const searchResults = await searchProperty({ streetAddress, zipCode });

    // Check if we found any properties
    if (!searchResults.items || searchResults.items.length === 0) {
      throw new Error('No properties found for the given address');
    }

    const property = searchResults.items[0];
    const clipId = property.clip;

    console.log('[CoreLogic] Found property with CLIP:', clipId);

    // Step 2: Get ownership details
    const ownershipData = await fetchOwnershipData(clipId);

    // Combine property and ownership data
    const combinedData = {
      ...property,
      ownership: ownershipData.data || ownershipData, // Handle both response structures
      searchMetadata: searchResults.metadata
    };

    console.log('[CoreLogic] Combined property and ownership data:', combinedData);

    return combinedData;

  } catch (err) {
    console.error('[CoreLogic] Request failed:', err);

    // In development, return mock data if API fails
    if (CURRENT_ENV === 'local') {
      console.log('[CoreLogic] Using mock data for development');
      return {
        ...MOCK_PROPERTY_DATA,
        ownership: MOCK_OWNERSHIP_DATA.data || MOCK_OWNERSHIP_DATA,
        mock: true // Flag to indicate this is mock data
      };
    }

    throw new Error(`CoreLogic API request failed: ${err.message}`);
  }
}

// TODO: Add fetchPropertyByGeocode({ lat, lng }) if CoreLogic supports it
// (see /v2/properties/search/geocode in Swagger)

// Environment Configuration:
// - Set REACT_APP_CURRENT_ENV='local' for local Next.js API (localhost:3000)
// - Set REACT_APP_CURRENT_ENV='production' for deployed Next.js API (delta-ops.vercel.app)
// - Note: UAT key is used for local development only. Do not use in production. 